import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

import { environment } from '../../environments/environment';

/**
 * Push channel from RFIJobOps (`/hubs/vendor-portal`): tells this page that the admin changed something for this job + vendor
 * (a counter, a reject for resubmission, an approval, a decline, a chat line ...). It carries no data — the page refetches
 * through its normal routes — and the page keeps working without it (refetch on focus), so every failure here is silent.
 */
@Injectable({ providedIn: 'root' })
export class VendorPortalLiveService implements OnDestroy {
  private connection: HubConnection | null = null;
  private joined: { jobKey: string; vendorKey: string } | null = null;

  /** Connects (or switches to another job/vendor) and calls `onChange` whenever the admin side changed something. */
  connect(jobKey: string, vendorKey: string, onChange: () => void): void {
    if (this.joined?.jobKey === jobKey && this.joined.vendorKey === vendorKey && this.connection) {
      return;
    }
    void this.disconnect();
    this.joined = { jobKey, vendorKey };

    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const connection = new HubConnectionBuilder()
      .withUrl(`${base}/hubs/vendor-portal`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.None)
      .build();
    this.connection = connection;

    connection.on('JobChanged', () => onChange());
    // After a dropped connection comes back, rejoin and catch up on anything missed meanwhile.
    connection.onreconnected(() => {
      void this.join(connection, jobKey, vendorKey).then(() => onChange());
    });

    connection
      .start()
      .then(() => this.join(connection, jobKey, vendorKey))
      .catch(() => {
        /* push is optional: focus/visibility refetch still keeps the page current */
      });
  }

  private async join(connection: HubConnection, jobKey: string, vendorKey: string): Promise<void> {
    if (connection.state !== HubConnectionState.Connected) {
      return;
    }
    try {
      await connection.invoke('JoinJob', jobKey, vendorKey);
    } catch {
      /* not assigned / link not valid: nothing to push */
    }
  }

  async disconnect(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    this.joined = null;
    if (connection) {
      try {
        await connection.stop();
      } catch {
        /* already stopped */
      }
    }
  }

  ngOnDestroy(): void {
    void this.disconnect();
  }
}
